"use client";

import { DEFAULT_PRIMARY_RGB, hexToRgb } from "@/lib/color/hex";
import { useEnterpriseTheme } from "@/lib/enterprise/EnterpriseProvider";
import { cn } from "@/lib/utils/index";
import { Check } from "lucide-react";
import { forwardRef, useId, useState } from "react";

interface FetchCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  label?: string;
  description?: string;
  className?: string;
  /** Accessible name when no visible `label` is rendered — e.g. a checkbox used standalone in a table cell. Overrides the label as the accessible name when both are given. */
  ariaLabel?: string;
  /** Explicit id for callers that need to reference the input (e.g. an external `<label htmlFor>` or a test selector). Falls back to an internally generated id — most callers never need this. */
  id?: string;
}

const sizeConfig = {
  sm: { box: "size-3.5", icon: "size-2.5", labelText: "text-xs", descriptionText: "text-xs" },
  md: { box: "size-4", icon: "size-3", labelText: "text-sm", descriptionText: "text-xs" },
  lg: { box: "size-5", icon: "size-3.5", labelText: "text-base", descriptionText: "text-sm" },
};

/**
 * Nowadays themed checkbox. Renders a custom checkmark (rather than the
 * native browser checkbox) so it tracks the active enterprise theme's
 * primary color, falling back to the default Nowadays indigo.
 */
export const FetchCheckbox = forwardRef<HTMLInputElement, FetchCheckboxProps>(
  ({ checked, onChange, disabled = false, size = "md", label, description, className, ariaLabel, id }, ref) => {
    const generatedId = useId();
    const { primaryColor, primaryHoverColor } = useEnterpriseTheme();
    const inputId = id ?? generatedId;
    const currentSize = sizeConfig[size];
    const interactionClasses = disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer";
    const [isFocusVisible, setIsFocusVisible] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const primaryRgb = hexToRgb(primaryColor) ?? DEFAULT_PRIMARY_RGB;
    const boxBg = disabled
      ? checked
        ? "#9ca3af"
        : "#ffffff"
      : checked
        ? isHovered
          ? primaryHoverColor
          : primaryColor
        : "#ffffff";
    const boxBorder = disabled
      ? checked
        ? "#9ca3af"
        : "#d1d5db"
      : checked
        ? isHovered
          ? primaryHoverColor
          : primaryColor
        : isHovered
          ? "#4b5563"
          : "#6b7280";

    return (
      <div className={cn("flex items-start gap-2", className)}>
        <span
          // mt-0.5 sets the box level with the label's first line; with no text beside it there is
          // nothing to line up with, and it would only sit the box a pixel below its row's centre.
          className={cn("group relative inline-flex shrink-0", (label || description) && "mt-0.5", currentSize.box)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <input
            ref={ref}
            id={inputId}
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            disabled={disabled}
            aria-label={ariaLabel}
            // `:focus-visible` (not plain focus) so the ring shows for keyboard navigation but not
            // a mouse click — matches native checkbox focus-ring behavior in modern browsers.
            onFocus={(e) => setIsFocusVisible(e.target.matches(":focus-visible"))}
            onBlur={() => setIsFocusVisible(false)}
            className={cn("absolute inset-0 z-10 m-0 size-full appearance-none", interactionClasses)}
          />
          <span
            aria-hidden="true"
            className={cn(
              "flex size-full items-center justify-center rounded border-2 transition-all duration-200 ease-out",
              !disabled && "group-active:scale-90",
              checked && "animate-checkbox-pop"
            )}
            style={{
              backgroundColor: boxBg,
              borderColor: boxBorder,
              // Full opacity (not the prior 0.5 alpha) — a translucent ring against a white page
              // background fails WCAG 1.4.11's 3:1 minimum for focus indicators.
              boxShadow: isFocusVisible
                ? `0 0 0 2px #ffffff, 0 0 0 4px rgb(${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b})`
                : "none",
            }}
          >
            <Check
              className={cn(currentSize.icon, "text-white", checked ? "scale-100 opacity-100" : "scale-50 opacity-0")}
              style={{
                transitionProperty: "transform, opacity",
                transitionDuration: "250ms",
                transitionTimingFunction: "cubic-bezier(0.34, 1.6, 0.64, 1)",
              }}
              strokeWidth={3}
            />
          </span>
        </span>
        {(label || description) && (
          <div className="flex flex-col">
            {label && (
              <label
                htmlFor={inputId}
                className={cn("select-none font-medium text-gray-700", currentSize.labelText, interactionClasses)}
              >
                {label}
              </label>
            )}
            {description && (
              <span className={cn("font-medium text-gray-500", currentSize.descriptionText, disabled && "opacity-50")}>
                {description}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
);

FetchCheckbox.displayName = "FetchCheckbox";
